import { Autocomplete, AutocompleteChangeReason, AutocompleteProps, SxProps, TextField, Theme } from "@mui/material";
import { MprcChip } from "components";
import { useMprcTheme } from "components/MprcThemeProvider";
import { isArray } from "lodash";
import { useRef, useState } from "react";

interface IFreeAutoCompleteProps {
    id?: string;
    label?: string;
    placeholder?: string;
    maxSelectItems?: number;
    multiSelect?: boolean;
    required?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    error?: boolean;
    helperText?: React.ReactNode;
    selectedItems: string[];
    inputRef?: React.Ref<any>;
    disabledInput?: boolean;
    margin?: MuiMargin;
    onChange: (item: string[]) => void;
    onKeyReturn?: (value: string) => void;
    // Ao pressionar enter após selecionar um item.
    onApply?: () => void;
    onError?: (error: any) => void;
    sx?: SxProps<Theme>;
}

type Props<T> = IFreeAutoCompleteProps &
    Omit<AutocompleteProps<T, boolean, boolean, boolean>,
        "value" | "options" | "renderInput" | "onChange" | "loading" | "isOptionEqualToValue" | "renderTags" | "helperText" | "multiple" | "disabled">;


export const MprcAutoCompleteFreeText = (props: Props<string>) => {

    const { theme } = useMprcTheme();

    const [inputValue, setInputValue] = useState<string>("");
    const [focused, setFocused] = useState<boolean>(false);

    const lastItemSelectd = useRef<string>("");

    const {
        selectedItems,
        maxSelectItems,
        label,
        placeholder,
        color,
        error,
        helperText,
        multiSelect = true,
        inputRef,
        disabledInput,
        onChange,
        onKeyReturn,
        onApply,
        required = false,
        startAdornment,
        endAdornment,
        autoFocus,
        disabled = false,
        margin,
        sx,
        id = new Date().getTime().toString(),
    } = props;

    // Verifica se pode mudar as propriedades e chama o onChange
    const handleChange = (e: React.SyntheticEvent, valueReceived: string[] | string | null, reason: AutocompleteChangeReason) => {
        if (valueReceived != null) {
            var value = isArray(valueReceived) ? valueReceived : [valueReceived];

            // Não permite excluir itens que não podem ser excluídos ao pressionar backspace
            if (reason == "removeOption" && e.type === "keydown" && selectedItems && selectedItems.length == 0)
                return;

            const lastItem = value && value.length > 0 ? value[value.length - 1] : null;
            lastItemSelectd.current = lastItem ?? "";

            if (multiSelect || (!multiSelect && selectedItems?.length == 0))
                onChange(value);
            else
                onChange(lastItem ? [lastItem] : []);

            tryClearInput();
        }
    };

    // Verifica se a tecla pressionada é o [Enter] e se não exitir sugestões chama evento
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (multiSelect && !!maxSelectItems && maxSelectItems > 0 && !!selectedItems && selectedItems.length == maxSelectItems && event.keyCode != 8) {
            event.preventDefault();
            return;
        }

        if (onKeyReturn && event.keyCode == 13) {
            event.preventDefault();
            onKeyReturn(inputValue);
            tryClearInput();
        }

        // Opção para aplicar em pesquisa, segundo enter quando tem itens selecionado e não tem sugestões.
        if (onApply && event.code == "Enter" && inputValue == "" && selectedItems.length > 0) {
            onApply();
        }
    };

    // Exclui o item pelo icone excluir do Chip
    const handleDeleteChip = (item: string | null) => {
        if (selectedItems && item) {
            const value = selectedItems.filter(x => x != item);
            onChange(value);
        }
    };


    function tryClearInput() {
        setInputValue("");
    }

    return (
        <Autocomplete
            id={id}
            autoFocus={autoFocus}
            fullWidth
            disableClearable={inputValue == ""}
            multiple={multiSelect}
            disabled={disabled}
            openText=""
            open={false}
            options={[]}
            clearOnEscape
            freeSolo
            loadingText={theme.MprcAutoComplete2.loadingText}
            inputValue={inputValue}
            value={selectedItems}
            onChange={handleChange}
            filterSelectedOptions={multiSelect}
            filterOptions={(options) => options}
            noOptionsText={theme.MprcAutoComplete2.noOptionsText}
            renderInput={(params) => (
                <TextField
                    {...params}
                    disabled={disabledInput}
                    error={error}
                    helperText={helperText}
                    variant="standard"
                    label={label}
                    color={color}
                    margin={margin}
                    placeholder={
                        selectedItems && selectedItems.length > 0
                            ? undefined
                            : focused
                                ? theme.MprcAutoCompleteFreeText.inputPlaceholder
                                : placeholder
                    }
                    onChange={(e) => {
                        if (disabledInput) return;
                        setInputValue(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setFocused(true);
                    }}
                    onBlur={() => {
                        setFocused(false);
                    }}
                    required={required}
                    fullWidth
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            style: { paddingRight: endAdornment ? 0 : undefined },
                            startAdornment: startAdornment || params.InputProps.startAdornment,
                            endAdornment: (
                                <>
                                    {endAdornment || params.InputProps.endAdornment}
                                </>
                            )
                        }
                    }}
                    sx={sx}
                    inputRef={inputRef}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    if (option) {
                        return (
                            <MprcChip
                                {...getTagProps({ index })}
                                key={`chip-${index}`}
                                label={option}
                                size="small"
                                onDelete={() => handleDeleteChip(option)}
                            />);
                    }
                    return null;
                })
            }
        />
    );
};